import React, { useEffect, useRef } from "react";
import Placeholder from "../../../images/Place Holder 3 1.webp";

const buildRenderList = (chats) => {
  const ordered = [...chats].reverse();
  const result = [];
  let i = 0;

  const PREFERENCE_KEYWORDS = [
    "please select your prefrence",
    "please select your preference",
  ];

  const isPreferenceText = (chat) =>
    chat.message_type === "text" &&
    PREFERENCE_KEYWORDS.includes(chat.message?.trim().toLowerCase());

  while (i < ordered.length) {
    const current = ordered[i];

    if (
      isPreferenceText(current) &&
      i + 1 < ordered.length &&
      ordered[i + 1]?.message_type === "button"
    ) {
      const headerText = current.message;
      i++;

      // Collect all consecutive buttons
      const group = [];
      while (i < ordered.length && ordered[i].message_type === "button") {
        group.push(ordered[i]);
        i++;
      }
      result.push({ kind: "button_group", buttons: group, headerText });
    }
    // Standalone buttons not preceded by preference text
    else if (current.message_type === "button") {
      const group = [];
      while (i < ordered.length && ordered[i].message_type === "button") {
        group.push(ordered[i]);
        i++;
      }
      if (group.length === 1) {
        result.push({ kind: "single_button", chat: group[0] });
      } else {
        result.push({
          kind: "button_group",
          buttons: group,
          headerText: "Please Select Your Preference",
        });
      }
    } else {
      result.push({ kind: "single", chat: current });
      i++;
    }
  }

  return result;
};

const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, li, arr) => {
    const parts = line.split(/(\*\*.*?\*\*)/g);
    return (
      <span key={li}>
        {parts.map((part, pi) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <strong key={pi}>{part.slice(2, -2)}</strong>
          ) : (
            <span key={pi}>{part}</span>
          ),
        )}
        {li < arr.length - 1 && <br />}
      </span>
    );
  });
};

const ChatWindow = ({ chats, isLoadingChats, chatHasMore, fetchMoreChats }) => {
  const chatContainerRef = useRef(null);
  const prevHeight = useRef(0);
  const bottomRef = useRef(null);
  const loadingOld = useRef(false);

  const handleScroll = (e) => {
    const container = e.currentTarget;
    if (container.scrollTop < 120 && chatHasMore && !isLoadingChats) {
      prevHeight.current = container.scrollHeight;
      loadingOld.current = true;
      fetchMoreChats();
    }
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (loadingOld.current) {
      container.scrollTop = container.scrollHeight - prevHeight.current;
      loadingOld.current = false;
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [chats]);

  const renderMessage = (chat) => {
    /* TEXT MESSAGE */
    if (chat.message_type === "text") {
      return (
        <p
          style={{
            whiteSpace: "pre-line",
            textAlign: "left",
            padding: "0px",
            maxWidth: "500px",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            margin: 0,
          }}
        >
          {renderMarkdown(chat.message)}
        </p>
      );
    }

    /* IMAGE MESSAGE */
    if (chat.message_type === "image") {
      return (
        <div
          style={{
            background: "#E7FDD8",
            padding: "12px",
            borderRadius: "8px",
            display: "inline-block",
            textAlign: "center",
          }}
        >
          <img
            src={chat.message}
            alt="chat"
            style={{
              width: "100%",
              maxWidth: "350px",
              minWidth: "250px",
              objectFit: "contain",
              borderRadius: "4px",
              background: "#fff",
              display: "block",
              margin: "0 auto",
            }}
            onError={(e) => {
              e.currentTarget.src = Placeholder;
            }}
          />
        </div>
      );
    }

    /* CATALOGUE MESSAGE */
    if (chat.message_type === "catalogue") {
      return (
        <div
          style={{
            borderRadius: "10px",
            overflow: "hidden",
            border: "1px solid #ddd",
            background: "#fff",
            textAlign: "left",
            padding: "10px",
            backgroundColor: "#D9FDD3",
          }}
        >
          {/* Reply — catalogue */}
          {chat.reply_to && (
            <div
              style={{
                border: "1px solid #ccc",
                borderLeft: "3px solid #01C37D",
                padding: "6px 10px",
                background: "#ffffff",
                marginBottom: 8,
                fontSize: 13,
                textAlign: "left",
                borderRadius: "4px",
                color: "#555",
              }}
            >
              {chat.reply_to}
            </div>
          )}

          {/* Header card: image + label */}
          <div style={{ display: "flex" }}>
            <img
              src="https://images.unsplash.com/photo-1550547660-d9450f859349"
              style={{
                width: "150px",
                height: "80px",
                objectFit: "cover",
                flexShrink: 0,
              }}
              alt="catalogue"
              onError={(e) => {
                e.currentTarget.src = Placeholder;
              }}
            />
            <div
              style={{
                padding: "10px",
                textAlign: "left",
                backgroundColor: "white",
                color: "black",
                display: "flex",
                alignItems: "center",
                fontSize: 13,
              }}
            >
              <b>Your Catalogue on WhatsApp</b>
            </div>
          </div>

          <div
            style={{
              padding: "10px",
              fontSize: "13px",
              textAlign: "left",
              lineHeight: "1.6",
              wordBreak: "break-all",
              whiteSpace: "pre-wrap",
              color: "#111",
              background: "#D9FDD3",
            }}
          >
            {chat.message}
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid #eee" }} />

          {/* View Catalogue button */}
          <div
            style={{
              padding: "10px",
              textAlign: "center",
              color: "#00a884",
              fontWeight: "600",
              cursor: "pointer",
              backgroundColor: "#ffffff",
              fontSize: 14,
            }}
            onClick={() => window.open(chat.message)}
          >
            View Catalogue
          </div>

          <div
            style={{
              fontSize: 14,
              color: "#667781",
              marginTop: 5,
              textAlign: "right",
            }}
          >
            {chat.datetime}
          </div>
        </div>
      );
    }

    /* DOCUMENT MESSAGE */
    if (chat.message_type === "document") {
      return (
        <a href={chat.message} target="_blank" rel="noreferrer">
          📄 View Document
        </a>
      );
    }

    /* VIDEO MESSAGE */
    if (chat.message_type === "video") {
      return (
        <video
          controls
          src={chat.message}
          style={{ width: "100%", borderRadius: 8 }}
        />
      );
    }

    /* TEMPLATE MESSAGE */
    if (chat.message_type === "template") {
      const header = chat.message?.components?.find((c) => c.type === "HEADER");
      const body = chat.message?.components?.find((c) => c.type === "BODY");
      const footer = chat.message?.components?.find((c) => c.type === "FOOTER");

      const headerImageUrl =
        header?.format === "IMAGE" && header?.example?.header_handle?.[0]
          ? header.example.header_handle[0]
          : header?.format === "IMAGE" && header?.parameters?.[0]?.image?.link
            ? header.parameters[0].image.link
            : null;

      return (
        <div style={{ maxWidth: 300, padding: "10px" }}>
          {header?.format === "IMAGE" && (
            <img
              src={headerImageUrl || Placeholder}
              alt="template"
              style={{
                width: "100%",
                height: 300,
                objectFit: "cover",
                borderRadius: 6,
                marginBottom: 12,
              }}
              onError={(e) => {
                e.currentTarget.src = Placeholder;
              }}
            />
          )}
          {body?.text && (
            <div
              style={{
                fontSize: 14,
                whiteSpace: "pre-wrap",
                lineHeight: "1.6",
                textAlign: "left",
              }}
            >
              {renderMarkdown(body.text)}
            </div>
          )}
          {footer?.text && (
            <div
              style={{
                fontSize: 16,
                marginTop: 6,
                color: "#667781",
                textAlign: "left",
              }}
            >
              {footer.text}
            </div>
          )}
        </div>
      );
    }

    return <p style={{ margin: 0 }}>{chat.message}</p>;
  };

  const renderSingleButton = (chat) => {
    const isSent = chat.sender === "1";
    return (
      <div
        key={chat.message_id}
        style={{ textAlign: isSent ? "right" : "left", marginBottom: 10 }}
      >
        <div
          style={{
            display: "inline-block",
            width: "100%",
            maxWidth: "350px",
            minWidth: "250px",
            borderRadius: 10,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            background: isSent ? "#ffffff" : "#d9fdd3",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              padding: "11px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: "#00a884",
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            {chat.message}
          </div>
        </div>
      </div>
    );
  };

  const renderButtonGroup = (buttons, headerText, idx) => {
    const isSent = buttons[0]?.sender === "1";
    return (
      <div
        key={`btn_group_${idx}`}
        style={{ textAlign: isSent ? "right" : "left", marginBottom: 10 }}
      >
        <div
          style={{
            display: "inline-block",
            width: "100%",
            maxWidth: "350px",
            minWidth: "250px",
            borderRadius: 10,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            background: isSent ? "#ffffff" : "#d9fdd3",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header — from original text message */}
          <div
            style={{
              padding: "10px 14px 2px",
              fontSize: 13,
              fontWeight: 500,
              color: "#111",
              textAlign: "left",
              background: "#d9fdd3",
            }}
          >
            {headerText}
          </div>

          {/* Timestamp below header */}
          <div
            style={{
              fontSize: 10,
              color: "#667781",
              padding: "0 14px 8px",
              textAlign: "right",
              background: "#d9fdd3",
            }}
          >
            {buttons[0]?.datetime}
          </div>

          {/* Each button with spacing */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "8px",
            }}
          >
            {buttons.map((btn, bIdx) => (
              <div
                key={btn.message_id || bIdx}
                style={{
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  padding: "11px 16px",
                  textAlign: "center",
                  fontSize: 14,
                  color: "#00a884",
                  fontWeight: 500,
                  cursor: "pointer",
                  background: "#ffffff",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#ffffff")
                }
              >
                {btn.message}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderList = buildRenderList(chats || []);

  return (
    <div
      ref={chatContainerRef}
      onScroll={handleScroll}
      className="hide-scrollbar"
      style={{ height: "100%", overflowY: "auto", padding: "20px" }}
    >
      {isLoadingChats && <div className="text-center py-2">Loading...</div>}

      {renderList.map((item, idx) => {
        /* ── BUTTON GROUP ── */
        if (item.kind === "button_group") {
          return renderButtonGroup(item.buttons, item.headerText, idx);
        }

        /* ── SINGLE BUTTON (Pay Now etc.) ── */
        if (item.kind === "single_button") {
          return renderSingleButton(item.chat);
        }

        /* ── NORMAL SINGLE MESSAGE ── */
        const chat = item.chat;
        const hideTimestamp = [
          "image",
          "video",
          "document",
          "template",
          "catalogue",
        ].includes(chat.message_type);

        return (
          <div
            key={chat.message_id}
            style={{
              textAlign: chat.sender === "1" ? "right" : "left",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: "inline-block",
                maxWidth: 350,
                padding: ["image", "video", "catalogue"].includes(
                  chat.message_type,
                )
                  ? 0
                  : 10,
                borderRadius: 10,
                background: chat.sender === "1" ? "#D9FDD3" : "#E7FDD8",
              }}
            >
              {/* REPLY MESSAGE */}

              {chat.reply_to && chat.message_type !== "catalogue" && (
                <div
                  style={{
                    border: "1px solid #ccc",
                    borderLeft: "3px solid #01C37D",
                    padding: "6px 10px",
                    background: "#ffffff",
                    marginBottom: 5,
                    fontSize: 13,
                    textAlign: "left",
                    borderRadius: "4px",
                    color: "#555",
                  }}
                >
                  {chat.reply_to}
                </div>
              )}

              {/* MESSAGE CONTENT */}
              {renderMessage(chat)}

              {/* TIMESTAMP */}
              {!hideTimestamp && (
                <div
                  style={{
                    fontSize: 10,
                    color: "#667781",
                    marginTop: 5,
                    textAlign: "right",
                  }}
                >
                  {chat.datetime}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
};

export default ChatWindow;
